import React, { useState, useEffect, useRef } from 'react';
import { FaBell, FaCalendarAlt, FaClock, FaUsers, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useCalendarNotifications } from '../hooks/useCalendarNotifications';
import { useNotificationCookies } from '../hooks/useNotificationCookies';

const GlobalNotifications = () => {
  const { persistentNotifications, unreadCount, markAsRead: markAsReadLocal } = useCalendarNotifications();
  const { markAsRead: markAsReadCookie, isNotificationRead } = useNotificationCookies();
  
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isWeeklyExpanded, setIsWeeklyExpanded] = useState(false);
  const [showNextWeek, setShowNextWeek] = useState(false);
  
  const panelRef = useRef(null);

  // ✅ FIX: Mostra TUTTE le notifiche, non solo quelle non lette
  const allNotifications = persistentNotifications || [];
  const unreadNotifications = allNotifications.filter(n => !isNotificationRead(n.id || n.CAL_Id_CalendarioEventi));

  // ✅ NEW: Counter solo per eventi di oggi
  const getTodayUnreadCount = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return unreadNotifications.filter(notification => {
      const eventTime = new Date(notification.scheduledTime);
      const eventDate = new Date(eventTime.getFullYear(), eventTime.getMonth(), eventTime.getDate());
      return eventDate.getTime() === today.getTime();
    }).length;
  };

  const todayUnreadCount = getTodayUnreadCount();

  // ✅ NEW: Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  useEffect(() => {
    const hasLoggedIn = sessionStorage.getItem('hasLoggedIn');
    if (hasLoggedIn && unreadNotifications.length > 0) {
      // showLoginToasts(unreadNotifications); // Commentato per evitare loop
      sessionStorage.removeItem('hasLoggedIn');
    }
  }, [unreadNotifications]);

  // ✅ FIX: Mostra componente se ci sono notifiche (lette o non lette)
  useEffect(() => {
    setIsVisible(allNotifications.length > 0);
  }, [allNotifications.length]);

  // ✅ IMPROVED: Funzione per categorizzare gli eventi con logica temporale corretta
  const categorizeNotifications = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const weekAfterNext = new Date(today);
    weekAfterNext.setDate(weekAfterNext.getDate() + 14);

    const dailyEvents = [];
    const currentWeekEvents = [];
    const nextWeekEvents = [];

    allNotifications.forEach(notification => {
      const eventTime = new Date(notification.scheduledTime);
      const eventDate = new Date(eventTime.getFullYear(), eventTime.getMonth(), eventTime.getDate());

      if (eventDate.getTime() === today.getTime()) {
        // Evento di oggi
        dailyEvents.push({
          ...notification,
          category: 'oggi',
          timeDisplay: getTimeDisplay(notification, now)
        });
      } else if (eventDate.getTime() === tomorrow.getTime()) {
        // Evento di domani
        dailyEvents.push({
          ...notification,
          category: 'domani',
          timeDisplay: getTimeDisplay(notification, now)
        });
      } else if (eventDate >= tomorrow && eventDate < nextWeek) {
        // Eventi della settimana corrente (escluso domani)
        currentWeekEvents.push({
          ...notification,
          category: 'questa settimana',
          timeDisplay: getTimeDisplay(notification, now)
        });
      } else if (eventDate >= nextWeek && eventDate < weekAfterNext) {
        // Eventi della prossima settimana
        nextWeekEvents.push({
          ...notification,
          category: 'prossima settimana',
          timeDisplay: getTimeDisplay(notification, now)
        });
      }
    });

    return { dailyEvents, currentWeekEvents, nextWeekEvents };
  };

  // ✅ FIXED: Logica colori coerente basata sulla urgenza temporale
  const getTimeDisplay = (notification, now) => {
    const eventTime = new Date(notification.scheduledTime);
    const eventDate = new Date(eventTime.getFullYear(), eventTime.getMonth(), eventTime.getDate());
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const timeDiff = Math.floor((eventTime.getTime() - now.getTime()) / 60000);

    let timeText = '';
    let urgencyColor = 'text-gray-600 dark:text-gray-400';
    let category = '';

    if (eventDate.getTime() === today.getTime()) {
      category = 'OGGI';
      if (timeDiff > 0) {
        if (timeDiff <= 30) {
          timeText = `tra ${timeDiff}m`;
          urgencyColor = 'text-red-600 dark:text-red-400'; // ROSSO: < 30 min
        } else if (timeDiff <= 60) {
          timeText = `tra ${timeDiff}m`;
          urgencyColor = 'text-orange-600 dark:text-orange-400'; // ARANCIONE: 30-60 min
        } else if (timeDiff <= 180) {
          const hours = Math.floor(timeDiff / 60);
          timeText = `tra ${hours}h`;
          urgencyColor = 'text-yellow-600 dark:text-yellow-400'; // GIALLO: 1-3 ore
        } else {
          const hours = Math.floor(timeDiff / 60);
          timeText = `tra ${hours}h`;
          urgencyColor = 'text-blue-600 dark:text-blue-400'; // BLU: > 3 ore
        }
      } else {
        // Eventi passati o in corso
        const absTimeDiff = Math.abs(timeDiff);
        const eventEndTime = new Date(notification.scheduledTime);
        eventEndTime.setHours(eventEndTime.getHours() + 1);
        const timeToEnd = Math.floor((eventEndTime.getTime() - now.getTime()) / 60000);
        
        if (timeToEnd > 0) {
          if (absTimeDiff <= 60) {
            timeText = `iniziato ${absTimeDiff}m fa`;
            urgencyColor = 'text-green-600 dark:text-green-400'; // VERDE: in corso
          } else {
            const hoursAgo = Math.floor(absTimeDiff / 60);
            timeText = `iniziato ${hoursAgo}h fa`;
            urgencyColor = 'text-green-600 dark:text-green-400'; // VERDE: in corso
          }
        } else {
          const timeSinceEnd = Math.abs(timeToEnd);
          if (timeSinceEnd <= 60) {
            timeText = `finito ${timeSinceEnd}m fa`;
            urgencyColor = 'text-gray-500 dark:text-gray-500'; // GRIGIO: finito
          } else {
            const hoursSinceEnd = Math.floor(timeSinceEnd / 60);
            timeText = `finito ${hoursSinceEnd}h fa`;
            urgencyColor = 'text-gray-500 dark:text-gray-500'; // GRIGIO: finito
          }
        }
      }
    } else if (eventDate.getTime() === tomorrow.getTime()) {
      category = 'DOMANI';
      timeText = `alle ${eventTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
      urgencyColor = 'text-purple-600 dark:text-purple-400'; // VIOLA: domani
    } else {
      // Date future
      category = eventTime.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' }).toUpperCase();
      timeText = `alle ${eventTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
      urgencyColor = 'text-indigo-600 dark:text-indigo-400'; // INDIGO: future
    }

    return { timeText, urgencyColor, category };
  };

  // ✅ FIX: Integra entrambi i sistemi di lettura
  const handleNotificationClick = async (notification) => {
    const notificationId = notification.id || notification.CAL_Id_CalendarioEventi;
    
    // Marca come letta nel sistema cookie (persistente)
    await markAsReadCookie(notificationId);
    
    // Marca come letta anche nel sistema locale (per aggiornamento UI immediato)
    markAsReadLocal(notificationId);
  };

  const handleMarkAllAsRead = async () => {
    const allIds = unreadNotifications.map(n => n.id || n.CAL_Id_CalendarioEventi);
    if (allIds.length > 0) {
      // Marca tutte come lette nel sistema cookie
      await markAsReadCookie(allIds);
      
      // Marca tutte come lette nel sistema locale
      allIds.forEach(id => markAsReadLocal(id));
    }
  };

  // Render single notification
  const renderNotification = (notification) => {
    const notificationId = notification.id || notification.CAL_Id_CalendarioEventi;
    const isRead = isNotificationRead(notificationId);
    
    return (
      <div
        key={notificationId}
        onClick={() => handleNotificationClick(notification)}
        className={`p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-all duration-200 ${
          isRead ? 'opacity-60' : 'opacity-100'
        }`}
      >
        <div className="flex items-start gap-3">
          <div className="relative">
            <FaCalendarAlt 
              size={16} 
              className={`mt-1 flex-shrink-0 ${
                isRead ? 'text-gray-400' : 'text-blue-500'
              }`} 
            />
            {!isRead && (
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className={`font-medium text-sm break-words ${
                isRead 
                  ? 'text-gray-500 dark:text-gray-500' 
                  : 'text-gray-900 dark:text-gray-100'
              }`}>
                {notification.title}
              </p>
              {!isRead && (
                <span className="inline-block w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0 mt-1.5"></span>
              )}
            </div>
            <p className={`text-xs mt-0.5 break-words ${
              isRead 
                ? 'text-gray-400 dark:text-gray-500' 
                : 'text-gray-600 dark:text-gray-400'
            }`}>
              {notification.message}
            </p>
            <div className="flex items-center gap-1.5 mt-1.5">
              <FaClock size={12} className={isRead ? 'text-gray-400' : notification.timeDisplay.urgencyColor} />
              <span className={`text-xs font-medium ${isRead ? 'text-gray-400' : notification.timeDisplay.urgencyColor}`}>
                {notification.timeDisplay.category}
              </span>
              <span className={`text-xs ${isRead ? 'text-gray-400' : notification.timeDisplay.urgencyColor}`}>
                {notification.timeDisplay.timeText}
              </span>
              {isRead && (
                <span className="text-xs text-gray-400 ml-auto">✓</span>
              )}
            </div>
            {notification.CAL_PartecipantiDettaglio?.count > 0 && (
              <div className="flex items-center gap-1.5 mt-1">
                <FaUsers size={12} className="text-gray-400" />
                <span className={`text-xs ${
                  isRead 
                    ? 'text-gray-400 dark:text-gray-500' 
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {notification.CAL_PartecipantiDettaglio.count} partecipanti
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!isVisible) return null;

  const { dailyEvents, currentWeekEvents, nextWeekEvents } = categorizeNotifications();
  const weeklyEvents = showNextWeek ? nextWeekEvents : currentWeekEvents;

  return (
    <div className="fixed top-4 right-4 z-50" ref={panelRef}>
      {/* Pulsante Notifiche */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`relative p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-105 ${
          todayUnreadCount > 0 
            ? 'bg-red-500 hover:bg-red-600 text-white' 
            : 'bg-gray-100 hover:bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
        }`}
        title={`${todayUnreadCount} notifiche di oggi non lette`}
      >
        <FaBell size={20} />
        {todayUnreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-white text-red-600 text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold shadow-sm">
            {todayUnreadCount > 99 ? '99+' : todayUnreadCount}
          </span>
        )}
      </button>

      {/* Panel Espanso */}
      {isExpanded && (
        <div 
          className="absolute top-16 right-0 max-h-[600px] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          style={{ width: '420px', minWidth: '420px' }}
        >
          {/* Header */}
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex justify-between items-center">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-base">
                Notifiche ({allNotifications.length})
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {todayUnreadCount} di oggi non lette • {unreadNotifications.length} totali non lette
              </p>
            </div>
            {unreadNotifications.length > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium px-2 py-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              >
                Segna tutte come lette
              </button>
            )}
          </div>

          {/* Content */}
          <div className="max-h-[520px] overflow-y-auto">
            {allNotifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <FaBell size={32} className="mx-auto mb-3 opacity-50" />
                <p className="text-sm">Nessuna notifica</p>
              </div>
            ) : (
              <div>
                {/* Sezione Eventi Giornalieri (Oggi e Domani) */}
                {dailyEvents.length > 0 && (
                  <div className="border-b border-gray-200 dark:border-gray-700">
                    <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
                      <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 flex items-center">
                        <FaCalendarAlt className="mr-2" size={14} />
                        Eventi Prossimi ({dailyEvents.length})
                      </h4>
                    </div>
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {dailyEvents.map(renderNotification)}
                    </div>
                  </div>
                )}

                {/* Sezione Eventi Settimanali */}
                {(currentWeekEvents.length > 0 || nextWeekEvents.length > 0) && (
                  <div>
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                          <FaClock className="mr-2" size={14} />
                          {showNextWeek ? 'Prossima Settimana' : 'Questa Settimana'} ({weeklyEvents.length})
                        </h4>
                        <div className="flex items-center gap-2">
                          {currentWeekEvents.length > 0 && nextWeekEvents.length > 0 && (
                            <button
                              onClick={() => setShowNextWeek(!showNextWeek)}
                              className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                            >
                              {showNextWeek ? 'Questa settimana' : 'Prossima settimana'}
                            </button>
                          )}
                          <button
                            onClick={() => setIsWeeklyExpanded(!isWeeklyExpanded)}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                          >
                            {isWeeklyExpanded ? 
                              <FaChevronUp size={12} className="text-gray-600 dark:text-gray-400" /> : 
                              <FaChevronDown size={12} className="text-gray-600 dark:text-gray-400" />
                            }
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {isWeeklyExpanded && weeklyEvents.length > 0 && (
                      <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {weeklyEvents.map(renderNotification)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalNotifications;
